import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { kidApi, apiError, KID_KEY, ACCESS_PAUSED_EVENT, storage } from "../../lib/api";
import { accessPaused } from "../../lib/access";
import useAccessDeadline from "../../hooks/useAccessDeadline";
import { Button, Coins, Modal } from "../../components/ui";
import Avatar from "../../components/Avatar";
import AdventureArt from "../../components/AdventureArt";
import AchievementBadge from "../../components/AchievementBadge";
import PlayerPassport from "../../components/PlayerPassport";
import KidRewardGoal from "../../components/KidRewardGoal";
import "./kids.css";
import IconTile from "../../components/IconTile";
import RewardVisual from "../../components/RewardVisual";
import KidChoreList, { KID_VIEW_KEY, KID_VIEWS } from "../../components/KidChoreList";
import Icon from "../../components/Icon";
import CoinBurst from "../../components/CoinBurst";
import { colorFor, KID_THEMES, PALETTE } from "../../lib/theme";
import { playCheer, playCoin, playPop, isMuted, setMuted } from "../../lib/sfx";

export default function KidDashboard() {
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [familyAccess, setFamilyAccess] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const isPaused = useAccessDeadline(familyAccess);
  const [chores, setChores] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [board, setBoard] = useState([]);
  const [tab, setTab] = useState("chores");
  const [goalId, setGoalId] = useState(null);
  const selectTab = (value) => { setTab(value); window.scrollTo({ top: 0, behavior: "auto" }); };
  const [loading, setLoading] = useState(true);
  const [burst, setBurst] = useState(false);
  const [burstLabel, setBurstLabel] = useState("Sent to grown-up!");
  const levelRef = useRef(null);
  const celebrationTimer = useRef(null);
  const [burstVariant, setBurstVariant] = useState("success");
  const celebrate = (label, variant = "success") => {
    clearTimeout(celebrationTimer.current);
    setBurstLabel(label); setBurstVariant(variant); setBurst(true);
    celebrationTimer.current = setTimeout(() => setBurst(false), 1800);
  };
  useEffect(() => () => clearTimeout(celebrationTimer.current), []);
  const [customizing, setCustomizing] = useState(false);
  const [choreView, setChoreViewState] = useState(() => {
    const v = storage.get(KID_VIEW_KEY);
    return KID_VIEWS.some(([k]) => k === v) ? v : "folders";
  });
  const setChoreView = (v) => { setChoreViewState(v); storage.set(KID_VIEW_KEY, v); };
  const [sfxOff, setSfxOff] = useState(isMuted());
  const toggleSfx = () => { const v = !sfxOff; setMuted(v); setSfxOff(v); if (!v) playPop(); };
  const saveCustom = async (patch) => {
    try {
      const { data } = await kidApi.put("/kids/customize", patch);
      setChild(data.child);
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const wearBadge = async (key) => {
    const current = child?.featured_badge;
    const saved = await saveCustom({ featured_badge: current === key ? "" : key });
    if (saved && current !== key) toast.success("Badge pinned to your profile! ⭐");
  };
  const [photoChore, setPhotoChore] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const m = await kidApi.get("/kids/me");
      setChild(m.data.child);
      setFamilyAccess(m.data.family_access);
      setLoadError(false);
      if (accessPaused(m.data.family_access)) return;
      const [c, r, l] = await Promise.all([kidApi.get("/kids/chores"), kidApi.get("/kids/rewards"), kidApi.get("/kids/leaderboard")]);
      setGoalId(storage.get(`cm_reward_goal_${m.data.child.id}`));
      setChild(m.data.child); setChores(c.data.chores); setRewards(r.data.rewards); setBoard(l.data.leaderboard);
      const lvl = m.data.child?.stats?.level;
      if (levelRef.current != null && lvl > levelRef.current) {
        celebrate(`Level ${lvl}!`, "level");
        playCheer();
      }
      levelRef.current = lvl;
    } catch (e) {
      if (e?.response?.status === 401) { storage.remove(KID_KEY); navigate("/kids"); }
      else if (e?.response?.status !== 402) setLoadError(true);
    } finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const pause = (event) => {
      if (event.detail?.tokenKey === KID_KEY) setFamilyAccess({ ...event.detail.access, has_access: false });
    };
    const refresh = () => { if (!document.hidden) load(); };
    const timer = setInterval(refresh, 60000);
    window.addEventListener(ACCESS_PAUSED_EVENT, pause);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener(ACCESS_PAUSED_EVENT, pause);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const exit = () => { storage.remove(KID_KEY); navigate("/kids"); };

  const openComplete = (c) => { playPop(); setPhotoChore(c); setPhotoFile(null); setPhotoPreview(null); };
  const closeComplete = () => { setPhotoChore(null); setPhotoFile(null); setPhotoPreview(null); };
  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const submitComplete = async (withPhoto) => {
    if (!photoChore) return;
    setSubmitting(true);
    try {
      let body = undefined;
      if (withPhoto && photoFile) {
        body = new FormData();
        body.append("photo", photoFile);
      }
      await kidApi.post(`/kids/chores/${photoChore.id}/complete`, body);
      closeComplete();
      celebrate("Sent to grown-up!");
      playCheer();
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };
  const redeem = async (r) => {
    try { await kidApi.post("/kids/redeem", { reward_id: r.id }); celebrate("Reward requested!", "reward"); playCoin(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (isPaused) return (
    <div className="min-h-screen bg-kidzone grid place-items-center p-6" data-testid="kid-access-paused">
      <main className="max-w-md w-full rounded-4xl bg-white border-2 border-primary/20 shadow-clay p-6 sm:p-10 text-center">
        <div className="mx-auto w-fit mb-5"><Avatar avatar={child?.avatar || "mouse"} color={child?.color} size={96} /></div>
        <span className="inline-flex items-center gap-2 rounded-full bg-sunny/20 text-sunny-fg text-xs font-black px-3 py-1 mb-3"><Icon name="bookmark" width={14} height={14} /> Progress saved</span>
        <h1 className="font-heading text-3xl font-black text-ink">Your adventure is paused</h1>
        <p className="text-ink-soft font-semibold mt-3 mb-6">Ask a grown-up to restart. Your coins, badges and missions will be here when you get back.</p>
        <Button onClick={load} data-testid="kid-paused-refresh" className="w-full">Check my adventure</Button>
        <button onClick={exit} className="mt-5 font-bold text-primary-dark underline" data-testid="kid-paused-exit">Switch explorer</button>
      </main>
    </div>
  );
  if (loadError && !child) return <div className="min-h-screen bg-kidzone grid place-items-center p-6"><div className="text-center"><p className="font-bold text-ink mb-4">We couldn't load your adventure. Try again in a moment.</p><Button onClick={load} data-testid="kid-load-retry">Try again</Button><button onClick={exit} data-testid="kid-load-exit" className="block mx-auto mt-4 underline">Back to explorers</button></div></div>;
  if (loading || !child) return <div className="min-h-screen bg-kidzone grid place-items-center"><div className="w-12 h-12 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  const s = child.stats;
  const theme = KID_THEMES[child.theme] || KID_THEMES.mint;
  const pct = Math.min(100, s.weekly_goal ? (s.weekly_points / s.weekly_goal) * 100 : 0);
  const todo = chores.filter((c) => c.status === "todo");
  const waiting = chores.filter((c) => c.status === "pending");
  const worn = (s.all_badges || []).find((b) => b.key === child.featured_badge && b.earned);
  const goal = rewards.find(r => r.id === goalId) || [...rewards].sort((a, b) => a.cost - b.cost).find(r => r.cost > s.balance) || rewards[0];
  const pinGoal = (reward) => {
    const key = `cm_reward_goal_${child.id}`;
    const next = goalId === reward.id ? null : reward.id;
    if (next) storage.set(key, next); else storage.remove(key);
    setGoalId(next);
  };
  const earnedBadges = (s.all_badges || []).filter(b => b.earned).length;

  return (
    <MotionConfig reducedMotion="user"><div className="kid-world min-h-screen pb-16 relative overflow-hidden" data-testid="kid-dashboard" style={{ backgroundColor: theme.bg }}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full blur-3xl opacity-40" style={{ background: theme.blobs[0] }} />
        <div className="absolute top-40 -left-16 w-56 h-56 rounded-full blur-3xl opacity-30" style={{ background: theme.blobs[1] }} />
        <div className="absolute bottom-10 right-0 w-56 h-56 rounded-full blur-3xl opacity-30" style={{ background: theme.blobs[2] }} />
      </div>
      <CoinBurst show={burst} label={burstLabel} variant={burstVariant} />
      <header className="kid-header sticky top-0 z-40 bg-white/90 backdrop-blur-xl px-5 py-4">
        <div className="kid-shell mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar avatar={child.avatar} color={child.color} size={48} />
            <div>
              <div className="font-heading font-black text-xl text-ink leading-tight break-words">{child.name}'s HQ</div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                <span className="text-xs font-black text-primary-dark bg-primary/10 rounded-full px-2 py-0.5">Lv {s.level} · {s.level_name}</span>
                {s.streak > 0 && <span className="text-sm font-bold text-coral flex items-center gap-0.5"><Icon name="flame" width={14} height={14} /> {s.streak}</span>}
                {worn && <span className="flex items-center gap-1 bg-sunny/20 rounded-full px-2 py-0.5 text-xs font-extrabold text-sunny-fg"><Icon name={worn.icon} width={12} height={12} /> {worn.label}</span>}
              </div>
            </div>
          </div>
          <div className="kid-header-actions flex items-center gap-2 shrink-0">
            <button onClick={toggleSfx} className="w-10 h-10 rounded-full bg-white grid place-items-center shadow-sm" data-testid="kid-sound-toggle" aria-label={sfxOff ? "Turn sound on" : "Turn sound off"} aria-pressed={!sfxOff}><Icon name={sfxOff ? "volume-x" : "volume-2"} className="text-ink-soft" width={18} height={18} /></button>
            <button onClick={() => setCustomizing(true)} className="w-10 h-10 rounded-full bg-white grid place-items-center shadow-sm" data-testid="kid-customize" aria-label="Customize"><Icon name="palette" className="text-ink-soft" width={18} height={18} /></button>
            <button onClick={exit} className="w-10 h-10 rounded-full bg-white grid place-items-center shadow-sm" data-testid="kid-exit" aria-label="Exit"><Icon name="log-out" className="text-ink-soft" width={18} height={18} /></button>
          </div>
        </div>
      </header>

      <div className="kid-shell mx-auto px-5 relative z-10">
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`quest-hero ${tab !== "chores" ? "quest-hero-compact" : ""}`} data-testid="coins-hero" aria-labelledby="mission-hq-title">
          <div className="quest-hero-copy">
            <span className="quest-eyebrow">YOUR EVERYDAY ADVENTURE</span>
            <h1 id="mission-hq-title">{tab === "chores" ? <>Little missions.<br /><span>Big possibilities.</span></> : { shop: "The reward shop", ranks: "Your crew", me: "Your explorer card" }[tab]}</h1>
            <p>Every helping hand makes your crew stronger.</p>
            <div className="quest-wallet">
              <span className="quest-coin" aria-hidden="true"><Icon name="zap" width={25} height={25} /></span>
              <div><strong data-testid="coins-hero-balance">{s.balance}</strong><span>coins to spend</span></div>
              {s.streak > 0 && <span className="quest-streak" data-testid="coins-hero-streak"><Icon name="flame" width={18} height={18} /> {s.streak} day streak</span>}
            </div>
          </div>
          <AdventureArt className="quest-hero-art" />
          <div className="quest-goal">
            <div className="flex items-center justify-between gap-3 mb-2"><span className="font-extrabold text-sm">Your weekly goal</span><span className="text-sm font-bold">{s.weekly_points} / {s.weekly_goal} coins</span></div>
            <div className="quest-progress" role="progressbar" aria-label="Weekly coin goal" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-valuetext={`${s.weekly_points} of ${s.weekly_goal} coins`}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
            </div>
            <div className="quest-goal-caption"><span>{pct >= 100 ? "Goal complete. Look at you go!" : "Coins count when your parent approves a mission."}</span><Icon name="flag" width={16} height={16} /></div>
          </div>
        </motion.section>

        <nav className="quest-nav" aria-label="Your adventure">
          {[["chores", "Missions", "compass"], ["shop", "Rewards", "gift"], ["ranks", "Crew", "trophy"], ["me", "Profile", "smile"]].map(([k, l, ic]) => (
            <button key={k} onClick={() => selectTab(k)} data-testid={`kid-tab-${k}`} aria-current={tab === k ? "page" : undefined} className={`quest-nav-button ${tab === k ? "is-active" : ""}`}>
              <Icon name={ic} width={20} height={20} /><span>{l}</span>
              {k === "chores" && todo.length > 0 && <span className="quest-nav-count">{todo.length}</span>}
            </button>
          ))}
        </nav>

        <AnimatePresence mode="wait">
          {tab === "chores" && (
            <motion.div key="chores" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <KidRewardGoal reward={goal} balance={s.balance} selected={goal?.id === goalId} onBrowse={() => selectTab("shop")} />
              <div className="quest-section-heading"><div><span className="quest-section-label">MAKE YOUR NEXT MOVE</span><h2>Your missions</h2></div><span className="quest-count">{todo.length} ready to go</span></div>
              {todo.length === 0 && waiting.length === 0 && (
                <div className="bg-white rounded-4xl p-10 text-center"><div className="mx-auto mb-4 w-fit"><IconTile name="flag" color="#7C3AED" size={72} /></div><p className="font-heading font-extrabold text-lg text-ink">All done!</p><p className="text-ink-soft font-medium">No chores right now. Nice!</p></div>
              )}
              <KidChoreList todo={todo} waiting={waiting} view={choreView}
                renderChore={(c) => (
                <div className="mission-card rounded-4xl border-2 shadow-clay p-6" data-testid="kid-chore" style={{ borderColor: `${colorFor(c.title)}55`, background: `linear-gradient(180deg, ${colorFor(c.title)}12, #ffffff 60%)` }}>
                  <div className="flex items-center gap-4 mb-4">
                    <IconTile name={c.icon || "check"} color={colorFor(c.title)} size={56} />
                    <div className="flex-1"><div className="font-heading font-extrabold text-xl text-ink leading-tight">{c.title}</div><Coins amount={c.points} className="text-sunny-fg text-lg mt-1" /></div>
                  </div>
                  <button onClick={() => openComplete(c)} data-testid={`kid-complete-${c.id}`} className="w-full py-4 rounded-full bg-primary-dark text-white font-heading font-black text-lg shadow-claybtn active:translate-y-1 active:shadow-none transition-transform flex items-center justify-center gap-2"><Icon name="check-check" width={22} height={22} /> Mission complete</button>
                </div>
                )}
                renderWaiting={(c) => (
                <div className="bg-white/60 rounded-4xl border-2 border-dashed border-primary/30 p-5 flex items-center gap-4" data-testid="kid-waiting">
                  <div className="w-12 h-12 rounded-2xl bg-sunny/20 grid place-items-center text-sunny-fg shrink-0 animate-pulse"><Icon name="hourglass" width={24} height={24} /></div>
                  <div className="flex-1"><div className="font-heading font-bold text-ink">{c.title}</div><div className="text-sm font-bold text-ink-muted">Sent for parent approval</div></div>
                  <Coins amount={c.points} className="text-ink-muted" />
                </div>
                )}
              />
            </motion.div>
          )}

          {tab === "shop" && (
            <motion.div key="shop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="quest-section-heading col-span-full"><div><span className="quest-section-label">YOU EARNED THIS</span><h2>The reward shop</h2><p>Pick something worth working toward.</p></div><IconTile name="gift" color="#FBBF24" size={56} /></div>
              {rewards.length === 0 && <div className="col-span-full bg-white rounded-4xl p-10 text-center font-bold text-ink">The shop is empty right now.</div>}
              {rewards.map((r) => {
                const afford = s.balance >= r.cost;
                return (
                  <div key={r.id} className={`reward-collectible ${goalId === r.id ? "is-wishlisted" : ""} bg-white rounded-4xl border-2 shadow-clay p-4 flex flex-col items-center text-center`} data-testid="kid-reward" style={{ borderColor: afford ? colorFor(r.id) : "#E2E8F0", background: afford ? `linear-gradient(180deg, ${colorFor(r.id)}14, #ffffff 62%)` : "#ffffff" }}>
                    <div className="reward-card-top"><span className={`reward-availability ${afford ? "is-ready" : ""}`}>{afford ? "IN REACH" : "KEEP EXPLORING"}</span><button type="button" onClick={() => pinGoal(r)} aria-pressed={goalId === r.id} aria-label={`${goalId === r.id ? "Remove" : "Set"} ${r.title} as reward goal`} className="reward-pin" data-testid={`kid-goal-${r.id}`}><Icon name="bookmark" width={17} height={17} fill={goalId === r.id ? "currentColor" : "none"} /></button></div>
                    <div className="reward-display"><RewardVisual reward={r} color={colorFor(r.id)} size={116} context="kid" muted={!afford} /></div>
                    <div className="font-heading font-extrabold text-ink leading-tight mb-1">{r.title}</div>
                    <Coins amount={r.cost} className="text-sunny-fg mb-3" />
                    <button disabled={!afford} onClick={() => redeem(r)} data-testid={`kid-redeem-${r.id}`} className={`mt-auto w-full py-2.5 rounded-full font-heading font-bold text-sm transition-transform active:scale-95 ${afford ? "bg-rose-700 text-white shadow-coral" : "bg-slate-100 text-ink-muted"}`}>{afford ? "Request reward" : `Need ${r.cost - s.balance} more`}</button>
                  </div>
                );
              })}
            </motion.div>
          )}

          {tab === "ranks" && (
            <motion.div key="ranks" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-white rounded-4xl border-2 border-sunny/30 shadow-clay p-5 space-y-2">
              <div className="crew-intro"><AchievementBadge icon="trophy" size={64} /><div><span className="quest-section-label">BETTER TOGETHER</span><h2>Your crew this week</h2><p><strong>{board.reduce((sum, r) => sum + r.weekly_points, 0)} coins</strong> earned together. Every helping hand counts.</p></div></div>
              {board.length >= 3 && <div className="crew-podium" data-testid="kid-crew-podium">{[1, 0, 2].map(i => <div key={board[i].id} className={`podium-player podium-place-${i + 1}`}><Avatar avatar={board[i].avatar} color={board[i].color} size={i === 0 ? 72 : 56} /><strong>{board[i].name}</strong><span>{board[i].weekly_points} coins</span><div className="podium-plinth"><Icon name={i === 0 ? "crown" : "award"} width={22} height={22} /><b>{i + 1}</b></div></div>)}</div>}
              {board.map((r, i) => {
                const medal = ["🥇", "🥈", "🥉"][i];
                return (
                  <div key={r.id} className={`flex items-center gap-3 p-3 rounded-2xl ${r.id === child.id ? "bg-primary/10" : ""}`}>
                    <div className={`w-9 h-9 rounded-full grid place-items-center font-black text-lg ${i === 0 ? "bg-sunny/20" : i === 1 ? "bg-slate-100" : i === 2 ? "bg-coral/20" : "bg-slate-50 text-ink-muted"}`}>{medal || (i + 1)}</div>
                    <Avatar avatar={r.avatar} color={r.color} size={40} ring={false} />
                    <div className="flex-1 min-w-0">
                      <span className="font-heading font-bold text-ink">{r.name}{r.id === child.id && " (you)"}</span>
                      {r.streak > 0 && <span className="ml-2 text-xs font-bold text-coral inline-flex items-center gap-0.5 align-middle"><Icon name="flame" width={12} height={12} />{r.streak}</span>}
                    </div>
                    <Coins amount={r.weekly_points} className="text-ink" />
                  </div>
                );
              })}
            </motion.div>
          )}
          {tab === "me" && (
            <motion.div key="me" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
              <PlayerPassport child={child} onCustomize={() => setCustomizing(true)} />

              <div className="bg-white rounded-4xl border-2 border-sunny/30 shadow-clay p-5">
                <div className="quest-section-heading"><div><span className="quest-section-label">YOUR ACHIEVEMENT COLLECTION</span><h2>Trophy case</h2></div><span className="quest-count">{earnedBadges} / {(s.all_badges || []).length}</span></div>
                <p className="text-sm text-ink-soft font-medium mb-5">Real effort. Well-earned badges. Pick one to wear on your explorer card.</p>
                <div className="achievement-grid" data-testid="kid-trophy-case">
                  {(s.all_badges || []).map((b) => {
                    const wornNow = child.featured_badge === b.key;
                    return (
                      <button key={b.key} disabled={!b.earned} onClick={() => wearBadge(b.key)} data-testid={`kid-badge-${b.key}`}
                        aria-pressed={wornNow} aria-label={`${b.label}${!b.earned ? `, locked: ${b.hint}` : wornNow ? ", wearing" : ", wear badge"}`}
                        className={`achievement-card ${b.earned ? "is-earned" : "is-locked"} ${wornNow ? "is-worn" : ""}`}>
                        <AchievementBadge icon={b.icon} earned={b.earned} size={80} />
                        <span className={`text-[11px] font-extrabold leading-tight ${b.earned ? "text-ink" : "text-ink-muted"}`}>{b.label}</span>
                        {!b.earned && <span className="text-[10px] font-bold text-ink-muted mt-0.5 leading-tight">{b.hint}</span>}
                        {wornNow && <span className="text-[10px] font-black text-sunny-fg mt-0.5">ON YOUR CARD</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Modal open={!!photoChore} onClose={closeComplete} title="Nice job! 🌟" testid="kid-complete-modal">
        {photoChore && (
          <div className="space-y-4">
            <p className="text-ink-soft font-semibold">Want to show a grown-up what you did? Add a photo (optional).</p>
            <div className="rounded-3xl border-2 border-dashed border-primary/30 bg-primary/5 overflow-hidden">
              {photoPreview ? (
                <img src={photoPreview} alt="Proof" className="w-full max-h-64 object-cover" data-testid="kid-photo-preview" />
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 py-10 cursor-pointer" data-testid="kid-photo-pick">
                  <div className="w-16 h-16 rounded-full bg-primary/15 grid place-items-center text-primary"><Icon name="camera" width={30} height={30} /></div>
                  <span className="font-heading font-bold text-ink">Take / choose a photo</span>
                  <input type="file" accept="image/*" capture="environment" onChange={pickPhoto} className="hidden" data-testid="kid-photo-input" />
                </label>
              )}
            </div>
            {photoPreview && (
              <label className="block text-center text-sm font-bold text-primary-dark cursor-pointer" data-testid="kid-photo-retake">
                Choose a different photo
                <input type="file" accept="image/*" capture="environment" onChange={pickPhoto} className="hidden" data-testid="kid-photo-retake-input" />
              </label>
            )}
            <div className="flex flex-col gap-2">
              {photoFile && (
                <Button onClick={() => submitComplete(true)} size="lg" className="w-full" disabled={submitting} data-testid="kid-submit-with-photo">
                  <Icon name="send" width={18} height={18} /> {submitting ? "Sending…" : "Send with photo"}
                </Button>
              )}
              <Button variant={photoFile ? "outline" : "primary"} onClick={() => submitComplete(false)} size="lg" className="w-full" disabled={submitting} data-testid="kid-submit-no-photo">
                {submitting && !photoFile ? "Sending…" : photoFile ? "Send without photo" : "Mark done (no photo)"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={customizing} onClose={() => setCustomizing(false)} title="Make it yours! 🎨" testid="kid-customize-modal">
        <div className="space-y-5">
          <div>
            <span className="block mb-2 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Background</span>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(KID_THEMES).map(([key, t]) => (
                <button key={key} onClick={() => saveCustom({ theme: key })} data-testid={`theme-${key}`} aria-pressed={child.theme === key}
                  className={`rounded-2xl p-3 border-2 ${child.theme === key ? "border-ink" : "border-transparent"}`} style={{ background: t.bg }}>
                  <div className="flex justify-center gap-1 mb-1">{t.blobs.map((b) => <span key={b} className="w-4 h-4 rounded-full" style={{ background: b }} />)}</div>
                  <span className="text-xs font-bold text-ink">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="block mb-2 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">My color</span>
            <div className="flex gap-2 flex-wrap">
              {PALETTE.map((c) => (<button key={c} onClick={() => saveCustom({ color: c })} data-testid={`kid-color-${c}`} className={`w-10 h-10 rounded-full border-4 ${child.color === c ? "border-ink" : "border-white"} shadow-sm`} style={{ background: c }} aria-label={c} />))}
            </div>
          </div>
          <div>
            <span className="block mb-2 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">How my chores look</span>
            <div className="flex gap-2">
              {KID_VIEWS.map(([k, l]) => (
                <button key={k} onClick={() => setChoreView(k)} data-testid={`chore-view-${k}`} aria-pressed={choreView === k}
                  className={`flex-1 rounded-2xl border-2 px-3 py-3 font-heading font-bold text-sm ${choreView === k ? "border-ink bg-primary/10" : "border-slate-200 bg-white"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Button className="w-full" onClick={() => setCustomizing(false)} data-testid="customize-done">Done</Button>
        </div>
      </Modal>

    </div></MotionConfig>
  );
}
